import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  X,
  Star,
  Check,
  XIcon,
  Clock,
  User,
  Calendar,
  MessageCircle,
  ChevronDown,
  ChevronUp,
  Filter,
  CheckSquare,
  Square,
  AlertTriangle,
  Trash2,
  Eye,
  MoreHorizontal,
  ThumbsUp,
  ThumbsDown,
  FileText,
  Sparkles
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { StarRating } from './ui/star-rating';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { Checkbox } from './ui/checkbox';
import { Textarea } from './ui/textarea';
import { formatDate, formatDateTime } from '../lib/utils';
import { Product, Review } from '../types';
import { 
  updateProductReview, 
  deleteProductReview, 
  batchUpdateProductReviews, 
  batchDeleteProductReviews 
} from '../services/productService';
import { ImagePreviewModal } from './ImagePreviewModal';

interface ReviewManagementModalProps {
  product: Product | null;
  isOpen: boolean;
  onClose: () => void;
  onReviewsUpdated?: (productId: string, updatedReviews: Review[]) => void;
}

type ReviewFilter = 'all' | 'pending' | 'verified' | 'rejected';

export function ReviewManagementModal({ 
  product, 
  isOpen, 
  onClose, 
  onReviewsUpdated 
}: ReviewManagementModalProps) {
  // Early return before any hooks to avoid Rules of Hooks violation
  if (!product) return null;

  const [filter, setFilter] = useState<ReviewFilter>('all');
  const [selectedReviews, setSelectedReviews] = useState<string[]>([]);
  const [expandedReview, setExpandedReview] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [batchActionLoading, setBatchActionLoading] = useState(false);
  const [reviews, setReviews] = useState<Review[]>(product.reviews || []);
  const [previewImages, setPreviewImages] = useState<string[]>([]);
  const [previewImageIndex, setPreviewImageIndex] = useState(0);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);

  // Update internal reviews state when product changes
  useEffect(() => {
    setReviews(product.reviews || []);
  }, [product.reviews]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setReviews(product.reviews || []);
      setSelectedReviews([]);
      setExpandedReview(null);
      setFilter('all');
    }
  }, [isOpen, product.reviews]);

  // Filter reviews based on current filter
  const filteredReviews = useMemo(() => {
    switch (filter) {
      case 'pending':
        return reviews.filter(review => review.status === 'pending');
      case 'verified':
        return reviews.filter(review => review.status === 'verified');
      case 'rejected':
        return reviews.filter(review => review.status === 'rejected');
      default:
        return reviews;
    }
  }, [reviews, filter]);

  // Statistics
  const stats = useMemo(() => {
    const pending = reviews.filter(r => r.status === 'pending').length;
    const verified = reviews.filter(r => r.status === 'verified').length;
    const rejected = reviews.filter(r => r.status === 'rejected').length;
    
    return { pending, verified, rejected, total: reviews.length };
  }, [reviews]);

  // Handle individual review action
  const handleReviewAction = async (reviewId: string, action: 'approve' | 'reject' | 'delete') => {
    setActionLoading(reviewId);
    
    try {
      if (action === 'delete') {
        await deleteProductReview(product.id, reviewId);
        toast.success('Review deleted successfully');
      } else {
        const status = action === 'approve' ? 'verified' as const : 'rejected' as const;
        await updateProductReview(product.id, reviewId, { status });
        toast.success(
          action === 'approve' 
            ? 'Review approved successfully' 
            : 'Review rejected successfully'
        );
      }
      
      // Update internal state and notify parent
      let updatedReviews: Review[];
      if (action === 'delete') {
        updatedReviews = reviews.filter(r => r.id !== reviewId);
      } else {
        updatedReviews = reviews.map(review => {
          if (review.id === reviewId) {
            return {
              ...review,
              status: action === 'approve' ? 'verified' as const : 'rejected' as const,
              updatedAt: new Date().toISOString(),
            };
          }
          return review;
        });
      }
      
      setReviews(updatedReviews);
      onReviewsUpdated?.(product.id, updatedReviews);
      
    } catch (error) {
      console.error('Error updating review:', error);
      toast.error('Failed to update review. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  // Handle batch actions
  const handleBatchAction = async (action: 'approve' | 'reject' | 'delete') => {
    if (selectedReviews.length === 0) return;
    
    setBatchActionLoading(true);
    
    try {
      if (action === 'delete') {
        await batchDeleteProductReviews(product.id, selectedReviews);
        toast.success(`${selectedReviews.length} reviews deleted successfully`);
      } else {
        const status = action === 'approve' ? 'verified' as const : 'rejected' as const;
        await batchUpdateProductReviews(product.id, selectedReviews, { status });
        toast.success(
          `${selectedReviews.length} reviews ${action === 'approve' ? 'approved' : 'rejected'} successfully`
        );
      }
      
      // Update internal state and notify parent
      let updatedReviews: Review[];
      
      if (action === 'delete') {
        updatedReviews = reviews.filter(r => !selectedReviews.includes(r.id));
      } else {
        updatedReviews = reviews.map(review => {
          if (selectedReviews.includes(review.id)) {
            return {
              ...review,
              status: action === 'approve' ? 'verified' as const : 'rejected' as const,
              updatedAt: new Date().toISOString(),
            };
          }
          return review;
        });
      }
      
      setReviews(updatedReviews);
      onReviewsUpdated?.(product.id, updatedReviews);
      
      // Clear selection
      setSelectedReviews([]);
      
    } catch (error) {
      console.error('Error batch updating reviews:', error);
      toast.error('Failed to update reviews. Please try again.');
    } finally {
      setBatchActionLoading(false);
    }
  };

  // Toggle review selection
  const toggleReviewSelection = (reviewId: string) => {
    setSelectedReviews(prev => 
      prev.includes(reviewId) 
        ? prev.filter(id => id !== reviewId)
        : [...prev, reviewId]
    );
  };

  // Select all visible reviews
  const selectAllVisible = () => {
    const visibleIds = filteredReviews.map(r => r.id);
    setSelectedReviews(visibleIds);
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedReviews([]);
  };

  const getStatusColor = (status: Review['status']) => {
    switch (status) {
      case 'verified':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: Review['status']) => {
    switch (status) {
      case 'verified':
        return <Check className="w-3 h-3" />;
      case 'pending':
        return <Clock className="w-3 h-3" />;
      case 'rejected':
        return <XIcon className="w-3 h-3" />;
      default:
        return <AlertTriangle className="w-3 h-3" />;
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-[10001] flex items-center justify-center p-4" style={{ margin: 0, padding: '1rem', top: 0, left: 0, right: 0, bottom: 0 }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ 
              type: "spring",
              duration: 0.5,
              bounce: 0.3 
            }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden"
          >
            {/* Enhanced Header */}
            <div className="relative bg-gradient-to-r from-blue-600 to-indigo-600 p-6 flex-shrink-0">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-transparent"></div>
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <motion.div 
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: "spring", bounce: 0.6 }}
                    className="h-12 w-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30"
                  >
                    <FileText className="h-6 w-6 text-white" />
                  </motion.div>
                  <div>
                    <motion.h2 
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 }}
                      className="text-2xl font-bold text-white"
                    >
                      Review Management
                    </motion.h2>
                    <motion.div 
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2 }}
                      className="flex items-center gap-4 text-white/80 text-sm mt-1"
                    >
                      <span className="font-medium">{product.name}</span>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-white/20 text-white border-white/30 hover:bg-white/30">
                          {stats.pending} pending
                        </Badge>
                        <Badge className="bg-white/20 text-white border-white/30 hover:bg-white/30">
                          {stats.verified} verified
                        </Badge>
                        <Badge className="bg-white/20 text-white border-white/30 hover:bg-white/30">
                          {stats.total} total
                        </Badge>
                      </div>
                    </motion.div>
                  </div>
                </div>
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={onClose}
                    className="text-white hover:bg-white/20 h-10 w-10 rounded-full"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </motion.div>
              </div>
            </div>

            {/* Enhanced Content */}
            <div className="flex-1 overflow-y-auto bg-gradient-to-b from-gray-50/50 to-white">
              {/* Controls */}
              <div className="px-6 py-4 border-b border-gray-200 bg-white">
                {/* Filter Tabs */}
                <motion.div 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="flex items-center gap-2"
                >
                  {[
                    { key: 'all' as ReviewFilter, label: 'All Reviews', count: stats.total },
                    { key: 'pending' as ReviewFilter, label: 'Pending', count: stats.pending },
                    { key: 'verified' as ReviewFilter, label: 'Verified', count: stats.verified },
                    { key: 'rejected' as ReviewFilter, label: 'Rejected', count: stats.rejected },
                  ].map((tab) => (
                    <motion.div
                      key={tab.key}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Button
                        variant={filter === tab.key ? "default" : "outline"}
                        size="sm"
                        onClick={() => setFilter(tab.key)}
                        className={`flex items-center gap-2 rounded-xl transition-all duration-300 ${
                          filter === tab.key 
                            ? "bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-600/25" 
                            : "hover:bg-gray-50 border-gray-200"
                        }`}
                      >
                        {tab.label}
                        <Badge 
                          variant="secondary" 
                          className={`${
                            filter === tab.key 
                              ? "bg-white/20 text-white" 
                              : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {tab.count}
                        </Badge>
                      </Button>
                    </motion.div>
                  ))}
                </motion.div>

                {/* Batch Actions */}
                {selectedReviews.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-4 pt-4 border-t border-gray-100"
                  >
                    <div className="flex items-center justify-between">
                      <Badge 
                        className="bg-blue-50 text-blue-700 border-blue-200 px-3 py-1.5 hover:bg-blue-100"
                      >
                        <Sparkles className="w-3 h-3 mr-1" />
                        {selectedReviews.length} review{selectedReviews.length !== 1 ? 's' : ''} selected
                      </Badge>
                      <div className="flex items-center gap-2">
                        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                          <Button
                            size="sm"
                            onClick={() => handleBatchAction('approve')}
                            disabled={batchActionLoading}
                            className="bg-green-600 hover:bg-green-700 text-white rounded-xl"
                          >
                            <ThumbsUp className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                        </motion.div>
                        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleBatchAction('reject')}
                            disabled={batchActionLoading}
                            className="border-red-200 text-red-600 hover:bg-red-50 rounded-xl"
                          >
                            <ThumbsDown className="w-4 h-4 mr-1" />
                            Reject
                          </Button>
                        </motion.div>
                        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleBatchAction('delete')}
                            disabled={batchActionLoading}
                            className="border-red-200 text-red-600 hover:bg-red-50 rounded-xl"
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Delete
                          </Button>
                        </motion.div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={clearSelection}
                          disabled={batchActionLoading}
                          className="text-gray-600 hover:text-gray-800"
                        >
                          Clear
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Select All */}
                {filteredReviews.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="select-all"
                        checked={selectedReviews.length === filteredReviews.length}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            selectAllVisible();
                          } else {
                            clearSelection();
                          }
                        }}
                      />
                      <label htmlFor="select-all" className="text-sm text-gray-600 cursor-pointer">
                        Select all visible reviews ({filteredReviews.length})
                      </label>
                    </div>
                  </div>
                )}
              </div>

              {/* Reviews List */}
              <div className="flex-1 overflow-y-auto">
                {filteredReviews.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.2 }}
                    >
                      <MessageCircle className="h-12 w-12 text-gray-400 mb-4" />
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        No {filter !== 'all' ? filter : ''} reviews found
                      </h3>
                      <p className="text-gray-600">
                        {filter === 'pending' 
                          ? 'There are no pending reviews for this product.'
                          : filter === 'verified'
                          ? 'There are no verified reviews for this product.'
                          : filter === 'rejected'
                          ? 'There are no rejected reviews for this product.'
                          : 'This product has no reviews yet.'
                        }
                      </p>
                    </motion.div>
                  </div>
                ) : (
                  <div className="p-6 space-y-4">
                    <AnimatePresence>
                      {filteredReviews.map((review, index) => (
                        <motion.div
                          key={review.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                          transition={{ duration: 0.3, delay: index * 0.05 }}
                          className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-md transition-all duration-300 hover:border-blue-200"
                        >
                          {/* Review Header */}
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex items-start gap-3">
                              <Checkbox
                                checked={selectedReviews.includes(review.id)}
                                onCheckedChange={() => toggleReviewSelection(review.id)}
                              />
                              
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center text-white text-sm font-medium shadow-lg">
                                    {review.name.charAt(0).toUpperCase()}
                                  </div>
                                  <div>
                                    <p className="font-medium text-gray-900">{review.name}</p>
                                    <p className="text-xs text-gray-500">{formatDateTime(new Date(review.date))}</p>
                                  </div>
                                </div>
                                
                                <div className="flex items-center gap-3 mb-3">
                                  <StarRating rating={review.rating} size="sm" />
                                  <Badge className={`${getStatusColor(review.status)} text-xs rounded-lg`}>
                                    {getStatusIcon(review.status)}
                                    <span className="ml-1 capitalize">{review.status}</span>
                                  </Badge>
                                </div>
                              </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex items-center gap-2">
                              {review.status === 'pending' && (
                                <>
                                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                    <Button
                                      size="sm"
                                      onClick={() => handleReviewAction(review.id, 'approve')}
                                      disabled={actionLoading === review.id}
                                      className="bg-green-600 hover:bg-green-700 text-white h-8 px-3 rounded-lg"
                                    >
                                      {actionLoading === review.id ? (
                                        <motion.div
                                          animate={{ rotate: 360 }}
                                          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                          className="w-3 h-3 border border-white border-t-transparent rounded-full"
                                        />
                                      ) : (
                                        <Check className="w-3 h-3" />
                                      )}
                                    </Button>
                                  </motion.div>
                                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleReviewAction(review.id, 'reject')}
                                      disabled={actionLoading === review.id}
                                      className="border-red-200 text-red-600 hover:bg-red-50 h-8 px-3 rounded-lg"
                                    >
                                      <XIcon className="w-3 h-3" />
                                    </Button>
                                  </motion.div>
                                </>
                              )}
                              
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0 rounded-lg hover:bg-gray-100">
                                    <MoreHorizontal className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="rounded-xl">
                                  <DropdownMenuItem
                                    onClick={() => setExpandedReview(
                                      expandedReview === review.id ? null : review.id
                                    )}
                                  >
                                    <Eye className="w-4 h-4 mr-2" />
                                    {expandedReview === review.id ? 'Collapse' : 'View Details'}
                                  </DropdownMenuItem>
                                  {review.status !== 'verified' && (
                                    <DropdownMenuItem
                                      onClick={() => handleReviewAction(review.id, 'approve')}
                                      disabled={actionLoading === review.id}
                                    >
                                      <Check className="w-4 h-4 mr-2" />
                                      Approve
                                    </DropdownMenuItem>
                                  )}
                                  {review.status !== 'rejected' && (
                                    <DropdownMenuItem
                                      onClick={() => handleReviewAction(review.id, 'reject')}
                                      disabled={actionLoading === review.id}
                                    >
                                      <XIcon className="w-4 h-4 mr-2" />
                                      Reject
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    onClick={() => handleReviewAction(review.id, 'delete')}
                                    disabled={actionLoading === review.id}
                                    className="text-red-600"
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </div>

                          {/* Review Content */}
                          <div className="ml-13">
                            <p className="text-gray-700 text-sm mb-3 leading-relaxed">{review.content}</p>
                            
                            {/* Review Images */}
                            {review.images && review.images.length > 0 && (
                              <div className="mb-4">
                                <p className="text-xs font-medium text-gray-600 mb-3 flex items-center">
                                  <span className="mr-2">Customer Photos ({review.images.length}):</span>
                                </p>
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                                  {review.images.map((image, index) => (
                                    <motion.div 
                                      key={index} 
                                      className="relative group cursor-pointer"
                                      whileHover={{ scale: 1.03 }}
                                      whileTap={{ scale: 0.98 }}
                                      transition={{ type: "spring", stiffness: 300, damping: 30 }}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setPreviewImages(review.images || []);
                                        setPreviewImageIndex(index);
                                        setIsPreviewModalOpen(true);
                                      }}
                                    >
                                      <img
                                        src={image}
                                        alt={`Review image ${index + 1}`}
                                        className="w-full h-36 object-cover rounded-xl border-2 border-gray-200 group-hover:border-blue-400 transition-all duration-300 group-hover:shadow-xl shadow-lg"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          setPreviewImages(review.images || []);
                                          setPreviewImageIndex(index);
                                          setIsPreviewModalOpen(true);
                                        }}
                                      />
                                      {/* Enhanced click indicator overlay */}
                                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 rounded-xl flex items-center justify-center pointer-events-none">
                                        <div className="bg-white/95 backdrop-blur-sm rounded-full p-3 transform scale-75 group-hover:scale-100 transition-all duration-300 shadow-lg">
                                          <Eye className="w-5 h-5 text-blue-600" />
                                        </div>
                                      </div>
                                      {/* Image count indicator */}
                                      {review.images && review.images.length > 1 && (
                                        <div className="absolute top-2 right-2 bg-black/80 text-white text-xs px-2 py-1 rounded-lg font-medium">
                                          {index + 1}/{review.images.length}
                                        </div>
                                      )}
                                      {/* Expand hint */}
                                      <div className="absolute bottom-2 left-2 bg-blue-600/90 text-white text-xs px-2 py-1 rounded-md opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-1 group-hover:translate-y-0">
                                        <span className="flex items-center">
                                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                                          </svg>
                                          View
                                        </span>
                                      </div>
                                    </motion.div>
                                  ))}
                                </div>
                                <div className="mt-3 p-2 bg-blue-50 rounded-lg border border-blue-200">
                                  <p className="text-xs text-blue-700 flex items-center">
                                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    Click any image to open the enhanced image viewer with zoom and rotation controls
                                  </p>
                                </div>
                              </div>
                            )}
                            
                            {/* Expanded Details */}
                            <AnimatePresence>
                              {expandedReview === review.id && (
                                <motion.div
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: 'auto' }}
                                  exit={{ opacity: 0, height: 0 }}
                                  transition={{ duration: 0.3 }}
                                  className="mt-4 pt-4 border-t border-gray-100 space-y-3"
                                >
                                  <div className="grid grid-cols-2 gap-4 text-xs text-gray-600">
                                    <div className="bg-gray-50 rounded-lg p-3">
                                      <span className="font-medium">Email:</span>
                                      <p className="mt-1">{review.email || 'Not provided'}</p>
                                    </div>
                                    <div className="bg-gray-50 rounded-lg p-3">
                                      <span className="font-medium">Submitted:</span>
                                      <p className="mt-1">{formatDateTime(new Date(review.date))}</p>
                                    </div>
                                    {review.updatedAt && (
                                      <div className="bg-gray-50 rounded-lg p-3">
                                        <span className="font-medium">Last Updated:</span>
                                        <p className="mt-1">{formatDateTime(new Date(review.updatedAt))}</p>
                                      </div>
                                    )}
                                    <div className="bg-gray-50 rounded-lg p-3">
                                      <span className="font-medium">Review ID:</span>
                                      <p className="font-mono mt-1">{review.id}</p>
                                    </div>
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}
      
      {/* Image Preview Modal */}
      <ImagePreviewModal
        images={previewImages}
        initialIndex={previewImageIndex}
        isOpen={isPreviewModalOpen}
        onClose={() => setIsPreviewModalOpen(false)}
        title="Review Images"
        description="Customer review photos"
      />
    </AnimatePresence>
  );
} 